import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getSessionId, getAccessToken } from '../utils/tokenStorage';
import { refreshAccessToken } from '../api/apiClient';

const SocketContext = createContext();

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const { isAuthenticated, user } = useAuth();

    useEffect(() => {
        // Only connect if user is authenticated
        if (isAuthenticated && user) {
            console.log("SocketContext: Initializing socket for user", user.email);

            // Extract the base URL safely
            let _apiUrl = process.env.REACT_APP_API_URL || '';
            let socketUrl = _apiUrl.endsWith('/api') ? _apiUrl.substring(0, _apiUrl.length - 4) : _apiUrl;

            // If the URL resolves to an empty string (meaning it was a relative path),
            // we MUST use a direct absolute URL to the backend. Otherwise socket.io will 
            // connect to the React dev server (port 3000) and get killed by the proxy.
            if (!socketUrl) {
                const isDev = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
                socketUrl = isDev ? 'http://localhost:5000' : window.location.origin;
            }

            console.log("SocketContext: Connecting to", socketUrl);

            const socketInstance = io(socketUrl, {
                withCredentials: true,
                transports: ['polling', 'websocket'], // polling first for proxy compatibility, then upgrade to websocket
                // Callback form: re-reads a fresh token on every (re)connect attempt
                auth: (cb) => cb({ token: getAccessToken() }),
            });

            socketInstance.on('connect', () => {
                console.log("SocketContext: Connected to server with ID", socketInstance.id);
                // Register session for concurrent login detection
                const userId = user?._id || user?.id;
                if (userId) {
                    const sessionId = getSessionId();
                    socketInstance.emit('register_session', { userId, sessionId });
                    console.log("SocketContext: Session registered for user", userId, "sessionId:", sessionId);
                }
            });

            socketInstance.on('connect_error', async (err) => {
                console.error("SocketContext: Connection error", err);
                // Access token expired between page load and handshake: refresh once, then reconnect.
                // Uses apiClient's working refresh (AuthContext.refreshToken is disabled).
                if (err?.message === 'AUTH_EXPIRED' && !socketInstance._authRetried) {
                    socketInstance._authRetried = true;
                    try {
                        await refreshAccessToken();
                        socketInstance.connect();
                    } catch { /* refreshAccessToken already fired auth:sessionExpired */ }
                }
            });

            // Handle force disconnect (e.g., admin kick or concurrent login override)
            socketInstance.on('force_disconnect', (data) => {
                console.warn("SocketContext: Force disconnect received:", data?.message);
                // Clear socket reference to prevent auto-reconnect
                setSocket(null);
                // The auth context should handle the actual logout/UI
                window.dispatchEvent(new CustomEvent('auth:forceLogout', {
                    detail: { message: data?.message || 'Votre session a été terminée.' }
                }));
            });

            setSocket(socketInstance);

            return () => {
                socketInstance.off('connect');
                socketInstance.off('connect_error');
                socketInstance.off('force_disconnect');
                socketInstance.disconnect();
            };
        } else if (socket) {
            socket.disconnect();
            setSocket(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, user?._id]); // reconnect if user changes

    return (
        <SocketContext.Provider value={socket}>
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = () => {
    return useContext(SocketContext);
};
